import { z } from 'zod';
import {
  ASSIGNMENT_REASONS,
  CHECKER_TYPES,
  CHECKLIST_SKIP_REASONS,
  CHECKLIST_SOURCES,
  CHECKLIST_STATUSES,
  CHECK_STATUSES,
  CRITICALITIES,
  EVIDENCE_RECORD_TYPES,
  EVIDENCE_TYPES,
  KPI_ENTRY_SOURCES,
  KPI_PENDING_REASONS,
  KPI_VERIFICATION_STATUSES,
  DISCREPANCY_STATUSES,
  type TargetPeriod,
} from '../enums/ptstdm';
import { isoDateTimeSchema, localDateSchema, singleLineText, uuidSchema } from './common';

/**
 * Primitives and DTOs shared by every PTSTDM contract file (Phase 5, PLAN §4 "PTSTDM core and daily
 * tracking"): decimal strings (D-068), date and period path parameters, the feed-availability
 * marker of the agenda and report, and the two DTOs that several modules return
 * (`ChecklistItemDto`, `KpiEntryDto`). Realtime payloads reuse these schemas (PLAN §5).
 */

// ---------------------------------------------------------------- primitives

/** D-068: KPI values, targets and percentages travel as decimal strings (numeric(18,4)). */
export const DECIMAL_PATTERN = /^-?\d{1,14}(\.\d{1,4})?$/;
export const decimalStringSchema = z
  .string()
  .regex(DECIMAL_PATTERN, 'Expected a decimal string such as "53" or "12.5"');
export const nonNegativeDecimalSchema = decimalStringSchema.refine(
  (value) => !value.startsWith('-'),
  'Must not be negative',
);
/** Request bodies may send a KPI value as a JSON number too; it is normalised to a string. */
export const decimalInputSchema = z
  .union([decimalStringSchema, z.number().finite()])
  .transform((value) => (typeof value === 'number' ? String(value) : value))
  .pipe(decimalStringSchema);

/** `HH:mm` (24 h), local to the workspace. */
export const hhmmSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Expected a time HH:mm');

/** `:date` of the daily and checklist endpoints: `YYYY-MM-DD` or `today` (the caller's local date). */
export const dailyDateParamSchema = z.union([localDateSchema, z.literal('today')]);
export type DailyDateParam = z.infer<typeof dailyDateParamSchema>;

/**
 * `:period` of the target endpoints: `{periodType}-{periodStart}`, e.g. `month-2026-10-01` or
 * `quarter-2026-10-01` (financial-year quarters start on the 1st of Apr, Jul, Oct and Jan, D-063).
 */
export const PERIOD_PARAM_PATTERN = /^(month|quarter)-(\d{4}-\d{2}-01)$/;
export const periodParamSchema = z
  .string()
  .regex(PERIOD_PARAM_PATTERN, 'Expected month-YYYY-MM-01 or quarter-YYYY-MM-01')
  .transform((value) => {
    const [, periodType, periodStart] = PERIOD_PARAM_PATTERN.exec(value)!;
    return {
      periodType: periodType as TargetPeriod,
      periodStart: periodStart!,
    };
  });
export type PeriodParam = z.infer<typeof periodParamSchema>;

/** Formats the `:period` path parameter. */
export function periodParam(periodType: 'month' | 'quarter', periodStart: string): string {
  return `${periodType}-${periodStart}`;
}

/** Plain-text descriptions (rich text editing of these fields arrives with the web editor). */
export const ptstdmDescriptionSchema = z.string().trim().max(2000);
/** SOP text: longer than a description. */
export const sopTextSchema = z.string().trim().max(20_000);
/** An SOP link: an https URL or an in-app path such as `/training/…`. */
export const sopUrlSchema = z
  .string()
  .trim()
  .max(2000)
  .regex(/^(https?:\/\/|\/(?!\/))/, 'Expected an http(s) URL or an in-app path');
/** Reasons the manager sees (lowered targets, late edits, not done). */
export const reasonSchema = singleLineText(1, 500);

/** `If-Match: "<version>"` (D-071); without it the write is last-write-wins. */
export const ifMatchHeaderSchema = z.object({
  'if-match': z
    .string()
    .regex(/^"?\d+"?$/, 'Expected "<version>"')
    .optional(),
});

// ---------------------------------------------------------------- feeds from other modules

/**
 * Sections of the agenda and report that other modules fill through ports (D-252): tasks (Phase 4,
 * wired by services/links/task-feed.ts, D-361), training and meetings (Phase 7). While a module is
 * not available its section is empty and says so, instead of looking like "nothing due".
 */
export const feedAvailabilitySchema = z.object({
  available: z.boolean(),
  /** The phase that ships the module, when not available. */
  availableFromPhase: z.number().int().nullable(),
  /** Shown in place of the section, e.g. "Tasks appear here once the tasks module is merged." */
  message: z.string().nullable(),
});
export type FeedAvailability = z.infer<typeof feedAvailabilitySchema>;

/** The task shape the agenda and report list (the tasks section's `TaskSummaryDto` subset). */
export const agendaTaskDtoSchema = z.object({
  id: uuidSchema,
  /** `T-1042`. */
  number: z.string(),
  kind: z.enum(['task', 'decision_request']),
  title: z.string(),
  status: z.string(),
  awaitingParty: z.enum(['owner', 'delegator']).nullable(),
  ownerUserId: uuidSchema,
  delegatedByUserId: uuidSchema.nullable(),
  priority: z.enum(['p1', 'p2', 'p3']),
  dueDate: localDateSchema.nullable(),
  dueTime: hhmmSchema.nullable(),
  kraId: uuidSchema.nullable(),
  daysOverdue: z.number().int().nonnegative().nullable(),
});
export type AgendaTaskDto = z.infer<typeof agendaTaskDtoSchema>;

// ---------------------------------------------------------------- ChecklistItemDto (PLAN §4)

export const checklistEvidenceDtoSchema = z.object({
  id: uuidSchema,
  evidenceType: z.enum(EVIDENCE_RECORD_TYPES),
  number: decimalStringSchema.nullable(),
  text: z.string().nullable(),
  url: z.string().nullable(),
  fileId: uuidSchema.nullable(),
  submittedByUserId: uuidSchema,
  createdAt: isoDateTimeSchema,
});
export type ChecklistEvidenceDto = z.infer<typeof checklistEvidenceDtoSchema>;

export const checklistItemDtoSchema = z.object({
  id: uuidSchema,
  source: z.enum(CHECKLIST_SOURCES),
  /** `recurring_task_definitions.id` (recurring items). */
  definitionId: uuidSchema.nullable(),
  /** `checklist_runs.id` (ad-hoc items). */
  runId: uuidSchema.nullable(),
  name: z.string(),
  kra: z.object({ id: uuidSchema, name: z.string() }).nullable(),
  positionId: uuidSchema.nullable(),
  /** The due date (after the non-working-day shift). */
  date: localDateSchema,
  occurrenceDate: localDateSchema,
  dueAt: isoDateTimeSchema,
  criticality: z.enum(CRITICALITIES),
  evidenceType: z.enum(EVIDENCE_TYPES),
  evidenceHint: z.string().nullable(),
  timeEstimateMinutes: z.number().int().nullable(),
  assigneeUserId: uuidSchema.nullable(),
  assignmentReason: z.enum(ASSIGNMENT_REASONS),
  originalAssigneeUserId: uuidSchema.nullable(),
  status: z.enum(CHECKLIST_STATUSES),
  skipReason: z.enum(CHECKLIST_SKIP_REASONS).nullable(),
  /** Latest row of each evidence type. */
  evidence: z.array(checklistEvidenceDtoSchema),
  /** Reason given for partial / not done. */
  reason: z.string().nullable(),
  completedAt: isoDateTimeSchema.nullable(),
  completedByUserId: uuidSchema.nullable(),
  check: z.object({
    checkerType: z.enum(CHECKER_TYPES),
    status: z.enum(CHECK_STATUSES),
    checkerUserId: uuidSchema.nullable(),
    at: isoDateTimeSchema.nullable(),
    note: z.string().nullable(),
  }),
  sop: z.object({ text: z.string().nullable(), url: z.string().nullable() }).nullable(),
  /** Ad-hoc items waiting for another item of their run. */
  blockedByItemId: uuidSchema.nullable(),
  escalatedAt: isoDateTimeSchema.nullable(),
  version: z.number().int().positive(),
});
export type ChecklistItemDto = z.infer<typeof checklistItemDtoSchema>;

// ---------------------------------------------------------------- KpiEntryDto (PLAN §4)

export const kpiEntryDtoSchema = z.object({
  id: uuidSchema,
  kpiId: uuidSchema,
  userId: uuidSchema,
  date: localDateSchema,
  /** Decimal string; paise for unit `inr` (D-068). The effective value (a correction wins). */
  value: decimalStringSchema,
  /** What the person claimed, when a verifier corrected it. */
  claimedValue: decimalStringSchema.nullable(),
  source: z.enum(KPI_ENTRY_SOURCES),
  /** "Calls: 47 · from telephony" and the source record behind a system value (SPEC §7.6). */
  sourceLabel: z.string().nullable(),
  sourceRef: z
    .object({
      integrationSourceId: uuidSchema.nullable(),
      externalId: z.string().nullable(),
      url: z.string().nullable(),
    })
    .nullable(),
  evidenceFileIds: z.array(uuidSchema),
  evidenceUrl: z.string().nullable(),
  note: z.string().nullable(),
  /** Trusted sources are verified by construction. */
  verificationStatus: z.enum(KPI_VERIFICATION_STATUSES),
  pendingReason: z.enum(KPI_PENDING_REASONS).nullable(),
  variance: z
    .object({ systemValue: decimalStringSchema, variancePct: decimalStringSchema })
    .nullable(),
  varianceFlag: z.boolean(),
  discrepancy: z
    .object({
      id: uuidSchema,
      claimedValue: decimalStringSchema,
      note: z.string(),
      status: z.enum(DISCREPANCY_STATUSES),
      at: isoDateTimeSchema,
    })
    .nullable(),
  enteredBy: uuidSchema.nullable(),
  enteredAt: isoDateTimeSchema,
  verifiedBy: uuidSchema.nullable(),
  verifiedAt: isoDateTimeSchema.nullable(),
  verificationNote: z.string().nullable(),
  revision: z.number().int().positive(),
});
export type KpiEntryDto = z.infer<typeof kpiEntryDtoSchema>;

/** Common path parameters (named after the resource, like the Phase 0 routes). */
export const positionIdParamsSchema = z.object({ positionId: uuidSchema });
export const dailyDateParamsSchema = z.object({ date: dailyDateParamSchema });
